import React, { FC, Dispatch, SetStateAction } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { List, Button, Tooltip, CircularProgress } from "@mui/material";
import QuantityUpdate from "../QtyUpdate/QuantityUpdate";
import { Delete } from "@mui/icons-material";
import DeleteErrorModal from "../RemoveEditDuplicate/DeleteErrorModal";
import { ChangeQtyFn } from "../QtyUpdate/QuantityUpdate";
import { UnitObj } from "../../util/quoteCartStorage";
import { styled } from "@mui/material/styles";
import useDnDSelector from "./useDnDSelector";
import UnitActions from "../UnitList/UnitActions";
import UnitDetails from "../UnitList/UnitDetails";
import allowedAlumAccess from "../../util/alumDealerList";

interface DragDropListProps {
  quoteId: string;
  status: string;
  units: UnitObj[];
  setUnits: Dispatch<SetStateAction<UnitObj[]>>;
  loadDuplicate: boolean;
  duplicateUnit: (unitId: string) => void;
  setWindowOrDoor: Dispatch<SetStateAction<string>>;
  // setWindowVal: Dispatch<SetStateAction<string>>;
  resetAllValues: () => void;
  setEditQuote: Dispatch<SetStateAction<boolean>>;
  checkedPD: boolean;
  changeQuantity: ChangeQtyFn;
  dealer: string;
  dealerEmail: string;
}

const StyledDeleteIcon = styled(Delete)({
  color: "red",
  position: "absolute",
  top: 10,
  left: 15,
  fontSize: 30,
});

const StyledCircularProgress = styled(CircularProgress)({
  color: "red",
  position: "absolute",
  top: 10,
  left: 15,
});

const StyledButton = styled(Button)({
  color: "white",
  position: "absolute",
  borderRadius: 5,
  top: 47,
  right: 50,
  width: 200,
  padding: 0,
});

const StyledDivRow = styled("div")({
  display: "flex",
  flexDirection: "row",
  padding: 10,
  paddingLeft: 0,
  paddingTop: 10,
  paddingBottom: 0,
  position: "relative",
  minHeight: 120,
});

export const reorder = (
  list: UnitObj[],
  startIndex: number,
  endIndex: number,
) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

export const getItemStyle = (
  isDragging: boolean,
  dragStyle: any,
  editBtn: boolean | undefined,
  id: number | string,
) => ({
  userSelect: "none",

  // change background colour if dragging
  position: "relative",
  backgroundColor: isDragging
    ? "lightgrey"
    : editBtn &&
        JSON.parse(sessionStorage.getItem("unitId") || JSON.stringify("")) ===
          id
      ? "darkgrey"
      : "",

  // styles we need to apply on draggables
  ...dragStyle,
});

const getListStyle = () => ({
  overflow: "auto",
  height: 0,
  flex: "1 0 auto",
});

const DragDropList: FC<DragDropListProps> = ({
  quoteId,
  status,
  units,
  setUnits,
  loadDuplicate,
  duplicateUnit,
  setWindowOrDoor,
  // setWindowVal,
  resetAllValues,
  setEditQuote,
  checkedPD,
  changeQuantity,
  dealer,
  dealerEmail,
}) => {
  const {
    mouseEnter,
    mouseLeave,
    removeUnits,
    handleCheck,
    onDragEnd,
    handleCloseModal,
    hoverId,
    showChecks,
    checkedIds,
    errorDel,
    loadingDel,
    isEdit,
  } = useDnDSelector(
    quoteId,
    units,
    setUnits,
    status,
    resetAllValues
  );

  return (
    <>
      {checkedIds.length ? (
        <>
          {!loadingDel ? (
            <Tooltip
              title={`Delete ${checkedIds.length} ${
                checkedIds.length === 1 ? "Unit" : "Units"
              }`}
            >
              <StyledDeleteIcon onClick={removeUnits} />
            </Tooltip>
          ) : (
            <StyledCircularProgress size={30} />
          )}
        </>
      ) : null}

      <DeleteErrorModal open={errorDel} handleClose={handleCloseModal} />

      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="quoteCart">
          {(provided, snapshot) => (
            <List
              style={getListStyle()}
              ref={provided.innerRef}
              {...provided.droppableProps}
            >
              {units.map((unit, index) => {
                const isEditDisabled = (() => {
                  //check if user has all aluminum access for a door that is all aluminum
                  const isAluminum = unit.material === "All Aluminum";
                  const allowAlumAccess = !allowedAlumAccess(
                    dealer,
                    dealerEmail,
                  );
                  if (isAluminum && allowAlumAccess) {
                    return true;
                  } else {
                    return false;
                  }
                })();

                return (
                  <Draggable
                    key={unit._id}
                    index={index}
                    draggableId={unit._id + "_dragID"}
                  >
                    {(provided, snapshot) => (
                      <StyledDivRow
                        onMouseEnter={() => mouseEnter(unit._id)}
                        onMouseLeave={mouseLeave}
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        style={getItemStyle(
                          snapshot.isDragging,
                          provided.draggableProps.style,
                          isEdit,
                          unit._id || "",
                        )}
                      >
                        <UnitActions
                          loadDuplicate={loadDuplicate}
                          duplicateUnit={duplicateUnit}
                          index={index}
                          setWindowOrDoor={setWindowOrDoor}
                          // setWindowVal={setWindowVal}
                          setEditQuote={setEditQuote}
                          unit={unit}
                          quoteId={quoteId}
                          handleDeleteCheck={handleCheck}
                          showChecks={showChecks}
                          hoverId={hoverId}
                          status={status}
                          isEditDisabled={isEditDisabled}
                        />
                        <UnitDetails unit={unit} checkedPD={checkedPD}/>
                        <QuantityUpdate
                          changeQuantity={changeQuantity}
                          unitId={unit._id || ""}
                          unitQuantity={unit.quantity}
                          isEditDisabled={isEditDisabled}
                        />
                      </StyledDivRow>
                    )}
                  </Draggable>
                );
              })}
              {provided.placeholder}
            </List>
          )}
        </Droppable>
      </DragDropContext>
    </>
  );
};

export default DragDropList;
